import { useState, type FormEvent } from "react";
import { useQuery } from "@tanstack/react-query";
import { X } from "lucide-react";
import type { CircleListResponse, SendAnnouncementResponse, UsersResponse } from "@mubin/contracts";
import { displayPhone } from "@mubin/contracts";
import { Empty, ErrorNote, Field, Loading, PageTitle, Tabs, useSubmit } from "../components/ui";
import { api, errorText, send } from "../lib/api";
import { useOrg } from "../lib/org";
import { useMe } from "../lib/session";

type Who = "student" | "teacher" | "staff";
const STAFF = ["supervisor", "branch_manager", "org_admin"] as const;

/**
 * Send a notification to chosen people (owner request 2026-10-18): in their app's notifications and as a push
 * on their phone, by WhatsApp from the organization's number (2026-10-22), or both. The server checks that every recipient is within the sender's reach.
 */
export function NotifyPage() {
  const { data: me } = useMe();
  const org = useOrg();
  const [who, setWho] = useState<Who>("student");
  const [circleId, setCircleId] = useState("");
  const [q, setQ] = useState("");
  const [picked, setPicked] = useState(new Map<string, string>());
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [done, setDone] = useState<string | null>(null);
  const [byApp, setByApp] = useState(true);
  const [byWhatsApp, setByWhatsApp] = useState(false);
  const { busy, error, run } = useSubmit();

  const params = new URLSearchParams({ status: "active" });
  if (who !== "staff") params.set("role", who);
  if (circleId && who !== "staff") params.set("circleId", circleId);
  const users = useQuery({
    queryKey: ["users", me?.activeRole, "notify", who, circleId],
    queryFn: () => api<UsersResponse>(`/v1/users?${params}`),
  });
  const circles = useQuery({
    queryKey: ["circles", me?.activeRole, "all", false],
    queryFn: () => api<CircleListResponse>("/v1/circles"),
  });
  const needle = q.trim();
  const rows = (users.data?.users ?? []).filter(
    (u) =>
      u.id !== me?.user.id &&
      (who !== "staff" || u.roles.some((r) => (STAFF as readonly string[]).includes(r.role))) &&
      (!needle ||
        u.name.includes(needle) ||
        displayPhone(u.phone).includes(needle) ||
        (u.phone ?? "").includes(needle)),
  );
  const allShown = rows.length > 0 && rows.every((u) => picked.has(u.id));

  const toggle = (id: string, name: string, on: boolean) =>
    setPicked((m) => {
      const next = new Map(m);
      if (on) next.set(id, name);
      else next.delete(id);
      return next;
    });
  const toggleShown = (on: boolean) =>
    setPicked((m) => {
      const next = new Map(m);
      for (const u of rows) {
        if (on) next.set(u.id, u.name);
        else next.delete(u.id);
      }
      return next;
    });

  function submit(e: FormEvent) {
    e.preventDefault();
    if (!org) return;
    void run(async () => {
      const r = await send<SendAnnouncementResponse>("POST", "/v1/notifications/announcements", {
        organizationId: org.id,
        userIds: [...picked.keys()],
        title: title.trim(),
        ...(body.trim() ? { body: body.trim() } : {}),
        channels: [...(byApp ? ["app"] : []), ...(byWhatsApp ? ["whatsapp"] : [])],
      });
      setDone(
        [
          byApp && `وصل في التطبيق إلى ${r.sent}`,
          byWhatsApp && `يُرسل عبر الواتساب إلى ${r.whatsapp} تباعًا`,
          r.noPhone > 0 && `${r.noPhone} بلا رقم جوال`,
        ]
          .filter(Boolean)
          .join(" · "),
      );
      setPicked(new Map());
      setTitle("");
      setBody("");
    }, errorText);
  }

  return (
    <>
      <PageTitle
        title="إرسال إشعار"
        sub="اختر المستلمين واكتب الرسالة، وأرسلها في التطبيق أو عبر الواتساب أو بالاثنين. يُسجَّل الإرسال في سجل التدقيق."
      />
      <section className="card">
        <h2>المستلمون</h2>
        <div className="toolbar">
          <Tabs
            label="نوع الحساب"
            value={who}
            onChange={setWho}
            items={[
              { value: "student", label: "الطلاب" },
              { value: "teacher", label: "المعلمون" },
              { value: "staff", label: "الإشراف والإدارة" },
            ]}
          />
          {who !== "staff" && (
            <select aria-label="الحلقة" value={circleId} onChange={(e) => setCircleId(e.target.value)}>
              <option value="">كل الحلقات</option>
              {(circles.data?.circles ?? []).map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name} · {c.branchName}
                </option>
              ))}
            </select>
          )}
          <input
            type="search"
            aria-label="بحث"
            placeholder="بحث بالاسم أو اسم المستخدم أو الجوال"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>
        {users.isPending ? (
          <Loading />
        ) : users.isError ? (
          <Empty title="تعذر التحميل">{errorText(users.error)}</Empty>
        ) : rows.length === 0 ? (
          <Empty title="لا توجد نتائج" />
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th style={{ width: "1%" }}>
                    <input
                      type="checkbox"
                      aria-label="تحديد الكل"
                      checked={allShown}
                      onChange={(e) => toggleShown(e.target.checked)}
                    />
                  </th>
                  <th>الاسم</th>
                  <th>الدخول</th>
                  <th>{who === "student" ? "الحلقة" : "الأدوار"}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((u) => {
                  const enrollment = u.enrollments.find((x) => x.status === "active");
                  return (
                    <tr
                      key={u.id}
                      className="clickable"
                      onClick={() => toggle(u.id, u.name, !picked.has(u.id))}
                    >
                      <td>
                        <input
                          type="checkbox"
                          aria-label={`تحديد ${u.name}`}
                          checked={picked.has(u.id)}
                          onClick={(e) => e.stopPropagation()}
                          onChange={(e) => toggle(u.id, u.name, e.target.checked)}
                        />
                      </td>
                      <td>{u.name}</td>
                      <td>
                        <span className="ltr muted small">{displayPhone(u.phone) || "—"}</span>
                      </td>
                      <td className="muted small">
                        {who === "student"
                          ? (enrollment?.circleName ?? "—")
                          : [...new Set(u.roles.map((r) => r.scopeName ?? ""))].filter(Boolean).join("، ")}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className="card">
        <h2>الرسالة</h2>
        {picked.size > 0 ? (
          <div className="chips" aria-label="المستلمون المختارون">
            {[...picked].map(([id, name]) => (
              <button
                key={id}
                type="button"
                className="btn btn-sm btn-secondary"
                onClick={() => toggle(id, name, false)}
              >
                {name} <X size={14} strokeWidth={1.75} aria-label="إزالة" />
              </button>
            ))}
          </div>
        ) : (
          <p className="muted small">لم تختر أحدًا بعد.</p>
        )}
        <form className="form" onSubmit={submit}>
          <Field id="notify-title" label="العنوان" hint="يظهر عنوانًا للإشعار على الجوال">
            <input
              id="notify-title"
              value={title}
              maxLength={80}
              required
              onChange={(e) => (setTitle(e.target.value), setDone(null))}
            />
          </Field>
          <Field id="notify-body" label="النص (اختياري)">
            <textarea
              id="notify-body"
              value={body}
              maxLength={500}
              rows={4}
              onChange={(e) => setBody(e.target.value)}
            />
          </Field>
          <fieldset className="field">
            <legend>طريقة الإرسال</legend>
            <label className="check">
              <input type="checkbox" checked={byApp} onChange={(e) => setByApp(e.target.checked)} />
              التطبيق (إشعار داخل التطبيق وعلى الجوال)
            </label>
            <label className="check">
              <input type="checkbox" checked={byWhatsApp} onChange={(e) => setByWhatsApp(e.target.checked)} />
              الواتساب (رسالة من رقم الجمعية)
            </label>
          </fieldset>
          <ErrorNote error={error} />
          {done && (
            <div className="muted" role="status">
              {done}
            </div>
          )}
          <div className="form-actions">
            <button
              className="btn btn-primary"
              disabled={busy || !org || picked.size === 0 || !title.trim() || (!byApp && !byWhatsApp)}
            >
              {picked.size > 0 ? `إرسال إلى ${picked.size}` : "إرسال"}
            </button>
          </div>
        </form>
      </section>
    </>
  );
}
